import {
  ContractInfo,
  HotTokensRowInfo,
  PoolInfo,
} from 'api/types/httpsTypes/contracts';
import { useQueryParams } from 'modules/routing/useQueryParams';
import React, { useState } from 'react';
import { ResizableBox, ResizeCallbackData } from 'react-resizable';
import { DexChart } from '../../../components/Chart/DexChart';
import GeckoTerminalPlaceHolder from '../../../../../assets/img/token/mockup-geckoterminal.png';
import {
  ChartBarSquareIcon,
  FireIcon,
  LightBulbIcon,
  PhoneIcon,
  PuzzlePieceIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { DexCard } from '../../../components/cards';
import { DexPanelButton } from '../../../components/tabs';
import { MainStatsComponent } from '../../stats/main-stats';
import { SmartStatsComponent } from '../../stats/smart-stats';
import { TradeStats } from '../../stats';
import { SmartSwapsComponent } from '../../stats/smart-swaps';
import * as yup from 'yup';
import { Chains, Protocols } from 'api/types/httpsTypes/d-wallets';
import { MoraliChartWidget } from '../../../components/Chart/MoraliChartWidget';
import { CallsInfo } from '../../stats/callInfo';
import { twMerge } from 'tailwind-merge';

export enum AnalyticsKeys {
  Security = 'security',
  SmartSwaps = 'smart-swaps',
  MainStats = 'main-stats',
  SmartStats = 'smart-stats',
  TelegramCalls = 'telegram-calls',
}

export function useSchemaPage() {
  return {
    activeAnalytics: yup
      .string()
      .default(AnalyticsKeys.MainStats)
      .oneOf(Object.values(AnalyticsKeys)),
  };
}

export function TokenInfoTradeBody({
  contract,
  selectedPool,
  poolsInfoFromBe,
}: {
  contract: ContractInfo;
  selectedPool: PoolInfo | undefined;
  poolsInfoFromBe: HotTokensRowInfo[] | undefined;
}) {
  const [queryParams, setQueryParams] = useQueryParams(useSchemaPage());
  const savedHeight = window.localStorage.getItem('chartHeight') || 350;
  const [chartHeight, setChartHeight] = useState(Number(savedHeight));

  const handleResize = (_e: React.SyntheticEvent, data: ResizeCallbackData) => {
    const newHeight = data.size.height;
    setChartHeight(newHeight);
    document
      .querySelector('.dex-chart-wrapper')
      ?.classList.remove('pointer-events-none');
  };

  return (
    <>
      <div className="space-y-1 flex flex-col grow">
        <ResizableBox
          height={chartHeight}
          width={Infinity}
          axis="y"
          handle={
            <div className="resize-handle bg-dex-black-700 h-2 w-full cursor-row-resize" />
          }
          minConstraints={[200, 100]}
          maxConstraints={[Infinity, 1000]}
          resizeHandles={['s']}
          onResizeStart={() => {
            document
              .querySelector('.dex-chart-wrapper')
              ?.classList.add('pointer-events-none');
          }}
          onResizeStop={handleResize}
        >
          {selectedPool ? (
            <div className="xxs:h-full">
              {contract &&
                (selectedPool.dex.key === Protocols.PumpFun ? (
                  <MoraliChartWidget
                    chartHeight={chartHeight}
                    contract={contract}
                  />
                ) : (
                  <DexChart
                    contract={contract}
                    poolAddress={selectedPool.poolAddress}
                  />
                ))}
            </div>
          ) : (
            <div className="overflow-x-hidden">
              <div
                style={{ height: chartHeight }}
                className="w-full xxs:bg-dex-black-900 flex items-center justify-center"
              >
                <img
                  src={GeckoTerminalPlaceHolder}
                  className="h-full"
                  style={{ filter: 'blur(5px)' }}
                  alt="Blurred Image"
                />
                <div className="absolute xxs:text-dex-white-secondary top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center z-10">
                  <PuzzlePieceIcon className="h-10 w-10" />
                  <p className="text-2xl font-bold">
                    {contract.status === 'RUG_PULL'
                      ? 'This token has been rug pulled'
                      : 'This token has not been launched yet'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </ResizableBox>
        <DexCard className="xxs:p-0 grow flex xxs:rounded">
          <div
            className={twMerge(
              'hidden lg:grid xxs:gap-1',
              contract.generalInfo.chain.key !== Chains.Solana
                ? 'lg:grid-cols-5'
                : 'lg:grid-cols-4'
            )}
          >
            <DexPanelButton
              className="xxs:p-2 xxs:rounded-b-none xxs:rounded-r-none"
              onClick={() =>
                setQueryParams({
                  activeAnalytics: AnalyticsKeys.MainStats,
                })
              }
              isActive={queryParams.activeAnalytics === AnalyticsKeys.MainStats}
            >
              <div className="flex space-x-1 items-center justify-center">
                <ChartBarSquareIcon className="h-4 w-4" />
                <div>Main Stats</div>
              </div>
            </DexPanelButton>
            <DexPanelButton
              className="xxs:p-2 xxs:rounded-none"
              onClick={() =>
                setQueryParams({
                  activeAnalytics: AnalyticsKeys.SmartStats,
                })
              }
              isActive={
                queryParams.activeAnalytics === AnalyticsKeys.SmartStats
              }
            >
              <div className="flex space-x-1 items-center justify-center">
                <FireIcon className="h-4 w-4" />
                <div>Smart Stats</div>
              </div>
            </DexPanelButton>
            <DexPanelButton
              className="xxs:p-2 xxs:rounded-none"
              onClick={() =>
                setQueryParams({
                  activeAnalytics: AnalyticsKeys.Security,
                })
              }
              isActive={queryParams.activeAnalytics === AnalyticsKeys.Security}
            >
              <div className="flex space-x-1 items-center justify-center">
                <ShieldCheckIcon className="h-4 w-4" />
                <div>Security</div>
              </div>
            </DexPanelButton>

            <DexPanelButton
              className="xxs:p-2 xxs:rounded-none"
              onClick={() =>
                setQueryParams({
                  activeAnalytics: AnalyticsKeys.SmartSwaps,
                })
              }
              isActive={
                queryParams.activeAnalytics === AnalyticsKeys.SmartSwaps
              }
            >
              <div className="flex space-x-1 items-center justify-center">
                <LightBulbIcon className="h-4 w-4" />
                <div>Smart Swaps</div>
              </div>
            </DexPanelButton>
            {contract.generalInfo.chain.key !== Chains.Solana && (
              <DexPanelButton
                className="xxs:p-2 xxs:rounded-b-none xxs:rounded-l-none"
                onClick={() =>
                  setQueryParams({
                    activeAnalytics: AnalyticsKeys.TelegramCalls,
                  })
                }
                isActive={
                  queryParams.activeAnalytics === AnalyticsKeys.TelegramCalls
                }
              >
                <div className="flex space-x-1 items-center justify-center">
                  <PhoneIcon className="h-4 w-4" />
                  <div>Telegram Calls</div>
                </div>
              </DexPanelButton>
            )}
          </div>

          {queryParams.activeAnalytics === AnalyticsKeys.MainStats && (
            <MainStatsComponent contract={contract} />
          )}

          {queryParams.activeAnalytics === AnalyticsKeys.SmartStats &&
            (poolsInfoFromBe?.[0] ? (
              <SmartStatsComponent stats={poolsInfoFromBe?.[0]} />
            ) : (
              <div className="xxs:p-2 overflow-y-scroll mt-1 mb-0 ">
                <div className="flex flex-col w-full lg:w-full h-full xxs:gap-0 lg:gap-1 ">
                  <div className="xxs:bg-dex-black-700 rounded-md xxs:p-2 w-full grid grid-cols-3 xxs:gap-1">
                    <></>
                  </div>
                </div>
              </div>
            ))}
          {queryParams.activeAnalytics === AnalyticsKeys.Security && (
            <TradeStats contract={contract} />
          )}
          {contract.generalInfo.chain.key !== Chains.Solana &&
            queryParams.activeAnalytics === AnalyticsKeys.TelegramCalls && (
              <CallsInfo contract={contract} />
            )}
          {queryParams.activeAnalytics === AnalyticsKeys.SmartSwaps && (
            <SmartSwapsComponent contract={contract} />
          )}
        </DexCard>
      </div>
    </>
  );
}
